import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
} from "@mui/material";
import {
    Warning as WarningIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
} from "@mui/icons-material";

interface ConflictModalProps {
    open: boolean;
    onClose: () => void;
    onMerge: () => void;
    onCreateNew: () => void;
    existingContact: {
        _id?: string;
        name?: string;
        email?: string;
        phone?: string;
    };
    incomingContact: {
        name?: string;
        email?: string;
        phone?: string;
    };
}

const ConflictModal: React.FC<ConflictModalProps> = ({
    open,
    onClose,
    onMerge,
    onCreateNew,
    existingContact,
    incomingContact,
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                },
            }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
                <WarningIcon color="warning" />
                <Typography variant="h6" fontWeight="bold">
                    Similar Contact Found
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        We found a similar contact in your records:
                    </Typography>
                    <Box
                        sx={{
                            bgcolor: "grey.50",
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "grey.300",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                            <PersonIcon fontSize="small" color="primary" />
                            <Typography variant="subtitle1" fontWeight="bold">
                                {existingContact.name || "Unknown"}
                            </Typography>
                        </Box>
                        {existingContact.email && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                <EmailIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                <Typography variant="body2" color="text.secondary">
                                    {existingContact.email}
                                </Typography>
                            </Box>
                        )}
                        {existingContact.phone && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <PhoneIcon fontSize="small" sx={{ color: "text.secondary" }} />
                                <Typography variant="body2" color="text.secondary">
                                    {existingContact.phone}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    What would you like to do?
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    color="inherit"
                    sx={{ borderRadius: 2 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onCreateNew}
                    variant="outlined"
                    color="primary"
                    sx={{ borderRadius: 2 }}
                >
                    Create New (Duplicate)
                </Button>
                <Button
                    onClick={onMerge}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2 }}
                >
                    Merge with Existing
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConflictModal;
